import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Mail, Users, FileText, Calendar, Send, Plus, Trash2, Edit, Search,
  Upload, ChevronDown, ChevronRight, Clock, CheckCircle, AlertCircle,
  BarChart3, Eye, MousePointer, Loader2, X, Tag
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VIEWS = ['overview', 'contacts', 'templates', 'campaigns', 'holidays'];

const STATUS_STYLES = {
  draft: 'bg-gray-800 text-gray-400',
  scheduled: 'bg-amber-900/30 text-amber-400',
  sending: 'bg-blue-900/30 text-blue-400',
  sent: 'bg-green-50 text-green-400',
  cancelled: 'bg-red-50 text-red-400',
};

export default function ClientEmailMarketing({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [view, setView] = useState('overview');
  const [stats, setStats] = useState(null);

  const hdrs = useCallback(() => ({
    ...getHeaders(),
    'Content-Type': 'application/json',
  }), [getHeaders]);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/email-stats`, { headers: getHeaders() })
      .then(r => r.json()).then(setStats).catch(console.error);
  }, [getHeaders, view]);

  return (
    <div className="space-y-5" data-testid="email-marketing-tab">
      {/* Sub-nav */}
      <div className="flex gap-1 bg-white rounded-lg p-1 border border-gray-200">
        {[
          { id: 'overview', label: t('emailMarketing.tabOverview'), icon: BarChart3 },
          { id: 'contacts', label: t('emailMarketing.tabContacts'), icon: Users },
          { id: 'templates', label: t('emailMarketing.tabTemplates'), icon: FileText },
          { id: 'holidays', label: t('emailMarketing.tabHolidays'), icon: Calendar },
          { id: 'campaigns', label: t('emailMarketing.tabCampaigns'), icon: Send },
        ].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
              view === v.id ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:text-gray-900 hover:bg-white/5'
            }`} data-testid={`email-view-${v.id}`}>
            <v.icon className="h-3.5 w-3.5" /> {v.label}
          </button>
        ))}
      </div>

      {view === 'overview' && <EmailOverview stats={stats} />}
      {view === 'contacts' && <ContactsView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === 'templates' && <TemplatesView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === 'campaigns' && <CampaignsView getHeaders={getHeaders} hdrs={hdrs} />}
      {view === 'holidays' && <HolidayPlannerView getHeaders={getHeaders} hdrs={hdrs} />}
    </div>
  );
}

// ═══════════════════ OVERVIEW ═══════════════════
function EmailOverview({ stats }) {
  if (!stats) return <div className="text-gray-400 animate-pulse text-center py-12">Loading stats...</div>;
  const cards = [
    { label: 'Total Contacts', value: stats.total_contacts, icon: Users, color: 'text-blue-400 bg-blue-900/30' },
    { label: 'Subscribed', value: stats.subscribed_contacts, icon: Mail, color: 'text-green-400 bg-green-50' },
    { label: 'Campaigns Sent', value: stats.sent_campaigns, icon: Send, color: 'text-cyan-400 bg-cyan-900/30' },
    { label: 'Scheduled', value: stats.scheduled_campaigns, icon: Clock, color: 'text-amber-400 bg-amber-900/30' },
    { label: 'Total Campaigns', value: stats.total_campaigns, icon: BarChart3, color: 'text-purple-400 bg-purple-900/30' },
    { label: 'Templates', value: stats.total_templates, icon: FileText, color: 'text-pink-400 bg-pink-900/30' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="email-overview">
      {cards.map((c, i) => (
        <Card key={i} className="bg-white border-gray-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${c.color}`}><c.icon className="h-4 w-4" /></div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{c.value}</p>
                <p className="text-[10px] text-gray-500">{c.label}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ═══════════════════ CONTACTS ═══════════════════
function ContactsView({ getHeaders, hdrs }) {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', phone: '', company: '', tags: [] });
  const [tagInput, setTagInput] = useState('');

  const fetchContacts = useCallback((s = '') => {
    setLoading(true);
    fetch(`${API}/api/client/dashboard/contacts?search=${s}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => { setContacts(d.contacts || []); setTotal(d.total || 0); setTags(d.tags || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API}/api/client/dashboard/contacts`, { method: 'POST', headers: hdrs(), body: JSON.stringify(form) });
    if (res.ok) { setShowAdd(false); setForm({ email: '', first_name: '', last_name: '', phone: '', company: '', tags: [] }); fetchContacts(); }
    else { const err = await res.json(); alert(err.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    await fetch(`${API}/api/client/dashboard/contacts/${id}`, { method: 'DELETE', headers: hdrs() });
    fetchContacts();
  };

  const handleImport = async (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return alert('Need header + data rows');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows = lines.slice(1).map(line => {
      const vals = line.split(',');
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i]?.trim() || ''; });
      return obj;
    });
    const res = await fetch(`${API}/api/client/dashboard/contacts/import`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ contacts: rows }) });
    const d = await res.json();
    alert(`Imported: ${d.imported}, Skipped: ${d.skipped}`);
    setShowImport(false);
    fetchContacts();
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(f => ({ ...f, tags: [...f.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  return (
    <div className="space-y-4" data-testid="contacts-view">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-500" />
          <Input value={search} onChange={e => { setSearch(e.target.value); fetchContacts(e.target.value); }}
            placeholder="Search contacts..." className="bg-white border-gray-200 text-gray-900 text-sm h-9 pl-9 placeholder:text-gray-600" data-testid="contact-search" />
        </div>
        <Badge className="bg-gray-800 text-gray-300 text-xs">{total} contacts</Badge>
        <Button size="sm" onClick={() => setShowImport(true)} className="bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs h-8" data-testid="import-contacts-btn">
          <Upload className="h-3 w-3 mr-1" /> Import CSV
        </Button>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="add-contact-btn">
          <Plus className="h-3 w-3 mr-1" /> Add Contact
        </Button>
      </div>

      {/* Import modal */}
      {showImport && (
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-900">Import Contacts (CSV format)</p>
              <Button variant="ghost" size="sm" onClick={() => setShowImport(false)} className="text-gray-500 h-6"><X className="h-3.5 w-3.5" /></Button>
            </div>
            <p className="text-[10px] text-gray-500 mb-2">Paste CSV with headers: email, first_name, last_name, phone, company</p>
            <textarea id="csv-input" rows={5} placeholder="email,first_name,last_name,phone,company&#10;john@example.com,John,Doe,555-1234,Acme Inc"
              className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-3 py-2 resize-none font-mono placeholder:text-gray-600" data-testid="csv-import-textarea" />
            <Button size="sm" onClick={() => handleImport(document.getElementById('csv-input').value)} className="bg-blue-600 hover:bg-blue-700 text-xs mt-2" data-testid="csv-import-submit">
              Import
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add form */}
      {showAdd && (
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-4 pb-4">
            <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="text-[10px] text-gray-500">Email *</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="contact-email" /></div>
              <div><Label className="text-[10px] text-gray-500">First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              <div><Label className="text-[10px] text-gray-500">Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              <div><Label className="text-[10px] text-gray-500">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              <div><Label className="text-[10px] text-gray-500">Company</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" /></div>
              <div>
                <Label className="text-[10px] text-gray-500">Tags</Label>
                <div className="flex gap-1 mt-0.5">
                  <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add tag..." className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 flex-1" />
                  <Button type="button" size="sm" onClick={addTag} className="bg-gray-700 h-8 px-2 text-xs">+</Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.tags.map((t, i) => (
                    <Badge key={i} className="bg-blue-50 text-blue-400 text-[10px] gap-0.5 pr-1">
                      {t}<button type="button" onClick={() => setForm(f => ({...f, tags: f.tags.filter((_, j) => j !== i)}))} className="ml-0.5">&times;</button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="md:col-span-3 flex gap-2">
                <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="contact-submit">Save Contact</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAdd(false)} className="border-gray-200 text-gray-400 text-xs h-8">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Contact list */}
      {loading ? <div className="text-gray-400 animate-pulse text-center py-8 text-sm">Loading...</div> : (
        <div className="space-y-1">
          {contacts.map(c => (
            <div key={c.contact_id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-gray-200 transition-colors" data-testid={`contact-${c.contact_id}`}>
              <div className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center text-blue-400 text-xs font-bold flex-shrink-0">
                {(c.first_name || c.email)[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{c.first_name} {c.last_name} {!c.first_name && !c.last_name ? c.email : ''}</p>
                <p className="text-[10px] text-gray-500 truncate">{c.email} {c.phone && `| ${c.phone}`}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {(c.tags || []).slice(0, 2).map((t, i) => (
                  <Badge key={i} className="bg-gray-800 text-gray-400 text-[9px]">{t}</Badge>
                ))}
                <Badge className={`text-[9px] ${c.subscribed ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'}`}>
                  {c.subscribed ? 'Subscribed' : 'Unsubscribed'}
                </Badge>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-500 hover:text-red-400" onClick={() => handleDelete(c.contact_id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
          {contacts.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">
              <Users className="h-10 w-10 mx-auto mb-2 text-gray-700" />
              No contacts yet. Add your first contact or import from CSV.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════ TEMPLATES ═══════════════════
function TemplatesView({ getHeaders, hdrs }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', subject: '', html_body: '', category: 'general' });
  const [editing, setEditing] = useState(null);

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/client/dashboard/email-templates`, { headers: getHeaders() })
      .then(r => r.json()).then(d => setTemplates(d.templates || []))
      .catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const handleSave = async (e) => {
    e.preventDefault();
    const url = editing
      ? `${API}/api/client/dashboard/email-templates/${editing}`
      : `${API}/api/client/dashboard/email-templates`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(form) });
    if (res.ok) { setShowForm(false); setEditing(null); setForm({ name: '', subject: '', html_body: '', category: 'general' }); fetch_(); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await fetch(`${API}/api/client/dashboard/email-templates/${id}`, { method: 'DELETE', headers: hdrs() });
    fetch_();
  };

  return (
    <div className="space-y-4" data-testid="templates-view">
      <div className="flex items-center justify-between">
        <Badge className="bg-gray-800 text-gray-300 text-xs">{templates.length} templates</Badge>
        <Button size="sm" onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', subject: '', html_body: '', category: 'general' }); }} className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="add-template-btn">
          <Plus className="h-3 w-3 mr-1" /> New Template
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-4 pb-4">
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-gray-500">Template Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="template-name" /></div>
                <div><Label className="text-[10px] text-gray-500">Subject Line *</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="template-subject" /></div>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Email Body (HTML)</Label>
                <textarea value={form.html_body} onChange={e => setForm({...form, html_body: e.target.value})} rows={8}
                  className="w-full mt-0.5 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-3 py-2 font-mono resize-none placeholder:text-gray-600" placeholder="<h1>Hello {{first_name}},</h1>..." data-testid="template-body" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="template-submit">{editing ? 'Update' : 'Create'} Template</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowForm(false)} className="border-gray-200 text-gray-400 text-xs h-8">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="text-gray-400 animate-pulse text-center py-8 text-sm">Loading...</div> : (
        <div className="grid gap-3">
          {templates.map(t => (
            <Card key={t.template_id} className="bg-white border-gray-200 hover:border-gray-200 transition-colors" data-testid={`template-${t.template_id}`}>
              <CardContent className="pt-4 pb-3 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50"><FileText className="h-4 w-4 text-blue-400" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 font-medium truncate">{t.name}</p>
                  <p className="text-[10px] text-gray-500">Subject: {t.subject}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 text-gray-400 hover:text-gray-900" onClick={() => { setEditing(t.template_id); setForm({ name: t.name, subject: t.subject, html_body: t.html_body, category: t.category }); setShowForm(true); }}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-gray-400 hover:text-red-400" onClick={() => handleDelete(t.template_id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {templates.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">
              <FileText className="h-10 w-10 mx-auto mb-2 text-gray-700" />
              No templates yet. Create your first email template.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════ CAMPAIGNS ═══════════════════
function CampaignsView({ getHeaders, hdrs }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testTarget, setTestTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', html_body: '', recipient_tags: [], scheduled_at: '' });
  const [tagInput, setTagInput] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/client/dashboard/email-campaigns`, { headers: getHeaders() })
      .then(r => r.json()).then(d => setCampaigns(d.campaigns || []))
      .catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const handleSave = async (e) => {
    e.preventDefault();
    const url = editing
      ? `${API}/api/client/dashboard/email-campaigns/${editing}`
      : `${API}/api/client/dashboard/email-campaigns`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(form) });
    if (res.ok) {
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', subject: '', html_body: '', recipient_tags: [], scheduled_at: '' });
      fetch_();
    }
  };

  const handleSchedule = async (id) => {
    const dt = prompt('Schedule date/time (ISO format, e.g., 2026-04-01T09:00:00):');
    if (!dt) return;
    await fetch(`${API}/api/client/dashboard/email-campaigns/${id}/schedule`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ scheduled_at: dt }) });
    fetch_();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    await fetch(`${API}/api/client/dashboard/email-campaigns/${id}`, { method: 'DELETE', headers: hdrs() });
    fetch_();
  };

  const handleSendNow = async (id, name) => {
    if (!window.confirm(`Send "${name}" to all recipients NOW?`)) return;
    setSending(id);
    try {
      const res = await fetch(`${API}/api/client/dashboard/email-campaigns/${id}/send`, { method: 'POST', headers: hdrs() });
      const data = await res.json();
      if (data.success) {
        // Poll for completion
        const poll = async (attempts = 0) => {
          if (attempts >= 30) { fetch_(); return; }
          await new Promise(r => setTimeout(r, 2000));
          const sr = await fetch(`${API}/api/client/dashboard/email-campaigns/${id}/status`, { headers: getHeaders() });
          const sd = await sr.json();
          if (sd.status === 'sent') { fetch_(); setSending(null); return; }
          poll(attempts + 1);
        };
        poll();
      }
    } catch (err) { console.error(err); setSending(null); }
  };

  const handleTestSend = async (id) => {
    if (!testEmail) return;
    try {
      const res = await fetch(`${API}/api/client/dashboard/email-campaigns/${id}/test-send`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify({ to_email: testEmail }),
      });
      const data = await res.json();
      alert(data.message || (data.success ? 'Test email sent!' : 'Failed'));
    } catch (err) { console.error(err); }
    setTestTarget(null);
    setTestEmail('');
  };

  const addTag = () => {
    if (tagInput.trim() && !form.recipient_tags.includes(tagInput.trim())) {
      setForm(f => ({ ...f, recipient_tags: [...f.recipient_tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  return (
    <div className="space-y-4" data-testid="campaigns-view">
      <div className="flex items-center justify-between">
        <Badge className="bg-gray-800 text-gray-300 text-xs">{campaigns.length} campaigns</Badge>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="add-campaign-btn">
          <Plus className="h-3 w-3 mr-1" /> New Campaign
        </Button>
      </div>

      {showForm && (
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-900">{editing ? 'Edit Campaign' : 'New Campaign'}</p>
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }} className="h-6 w-6 p-0 text-gray-500"><X className="h-3.5 w-3.5" /></Button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-gray-500">Campaign Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="campaign-name" /></div>
                <div><Label className="text-[10px] text-gray-500">Subject Line *</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" data-testid="campaign-subject" /></div>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Recipient Tags (leave empty for all contacts)</Label>
                <div className="flex gap-1 mt-0.5">
                  <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Filter by tag..." className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 flex-1" />
                  <Button type="button" size="sm" onClick={addTag} className="bg-gray-700 h-8 px-2 text-xs"><Tag className="h-3 w-3" /></Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.recipient_tags.map((t, i) => (
                    <Badge key={i} className="bg-blue-50 text-blue-400 text-[10px] gap-0.5 pr-1">
                      {t}<button type="button" onClick={() => setForm(f => ({...f, recipient_tags: f.recipient_tags.filter((_, j) => j !== i)}))}>&times;</button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] text-gray-500">Email Body (HTML)</Label>
                  {form.html_body && (
                    <button type="button" onClick={() => setPreviewTarget({ name: form.name || 'Preview', subject: form.subject, html_body: form.html_body })}
                      className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Eye className="h-3 w-3" /> Preview
                    </button>
                  )}
                </div>
                <textarea value={form.html_body} onChange={e => setForm({...form, html_body: e.target.value})} rows={8}
                  className="w-full mt-0.5 bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-3 py-2 font-mono resize-none placeholder:text-gray-600" placeholder="<h1>Hello,</h1>..." data-testid="campaign-body" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-8" data-testid="campaign-submit">{editing ? 'Update Campaign' : 'Create Campaign'}</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }} className="border-gray-200 text-gray-400 text-xs h-8">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Email Preview Modal */}
      {previewTarget && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setPreviewTarget(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="email-preview-modal">
            <div className="bg-gray-100 border-b px-5 py-3 flex items-center justify-between flex-shrink-0">
              <div>
                <p className="text-sm font-semibold text-gray-900">{previewTarget.name}</p>
                <p className="text-xs text-gray-500">Subject: {previewTarget.subject}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setPreviewTarget(null)} className="h-7 w-7 p-0 text-gray-500 hover:text-gray-900"><X className="h-4 w-4" /></Button>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                title="Email Preview"
                srcDoc={previewTarget.html_body || '<p style="padding:40px;color:#999;text-align:center;">No email content yet.</p>'}
                className="w-full h-full min-h-[400px] border-0"
                sandbox=""
                data-testid="email-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="text-gray-400 animate-pulse text-center py-8 text-sm">Loading...</div> : (
        <div className="space-y-2">
          {campaigns.map(c => (
            <Card key={c.campaign_id} className="bg-white border-gray-200" data-testid={`campaign-${c.campaign_id}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-50"><Send className="h-4 w-4 text-blue-400" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 font-medium truncate">{c.name}</p>
                    <p className="text-[10px] text-gray-500">
                      Subject: {c.subject} | Recipients: {c.recipient_count}
                      {c.scheduled_at && ` | Scheduled: ${new Date(c.scheduled_at).toLocaleString()}`}
                    </p>
                  </div>
                  <Badge className={`text-[9px] capitalize ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>{c.status}</Badge>
                  <div className="flex gap-1">
                    {(c.status === 'draft' || c.status === 'scheduled') && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 text-gray-400 hover:text-gray-900" title="Edit"
                          onClick={() => { setEditing(c.campaign_id); setForm({ name: c.name, subject: c.subject, html_body: c.html_body || '', recipient_tags: c.recipient_tags || [], scheduled_at: c.scheduled_at || '' }); setShowForm(true); }}
                          data-testid={`edit-campaign-${c.campaign_id}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-blue-400 hover:text-blue-300" title="Preview Email"
                          onClick={() => setPreviewTarget({ name: c.name, subject: c.subject, html_body: c.html_body })}
                          data-testid={`preview-campaign-${c.campaign_id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-cyan-400 hover:text-cyan-300" title="Test Send"
                          onClick={() => { setTestTarget(c.campaign_id); setTestEmail(''); }}
                          data-testid={`test-campaign-${c.campaign_id}`}>
                          <Mail className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-amber-400 hover:text-amber-300" onClick={() => handleSchedule(c.campaign_id)} title="Schedule">
                          <Clock className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-green-400 hover:text-green-300" onClick={() => handleSendNow(c.campaign_id, c.name)} disabled={sending === c.campaign_id} title="Send Now" data-testid={`send-campaign-${c.campaign_id}`}>
                          {sending === c.campaign_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        </Button>
                      </>
                    )}
                    {c.status === 'sent' && c.html_body && (
                      <Button size="sm" variant="ghost" className="h-7 text-blue-400 hover:text-blue-300" title="Preview Email"
                        onClick={() => setPreviewTarget({ name: c.name, subject: c.subject, html_body: c.html_body })}
                        data-testid={`preview-sent-${c.campaign_id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {c.status !== 'sending' && (
                      <Button size="sm" variant="ghost" className="h-7 text-gray-400 hover:text-red-400" onClick={() => handleDelete(c.campaign_id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Test Send Input */}
                {testTarget === c.campaign_id && (
                  <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200/50">
                    <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Test email address" className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-7 flex-1" data-testid={`test-email-input-${c.campaign_id}`} />
                    <Button size="sm" onClick={() => handleTestSend(c.campaign_id)} className="bg-blue-600 hover:bg-blue-700 text-xs h-7 px-3" data-testid={`test-send-btn-${c.campaign_id}`}>Send Test</Button>
                    <Button size="sm" variant="ghost" onClick={() => setTestTarget(null)} className="h-7 text-gray-500 px-2"><X className="h-3 w-3" /></Button>
                  </div>
                )}
                {c.status === 'sending' && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200/50">
                    <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin" />
                    <span className="text-xs text-blue-400">Sending to {c.recipient_count} recipients...</span>
                  </div>
                )}
                {c.status === 'sent' && c.stats && (
                  <div className="flex gap-4 mt-2 pt-2 border-t border-gray-200/50">
                    {[
                      { label: 'Sent', value: c.stats.sent },
                      { label: 'Opened', value: c.stats.opened },
                      { label: 'Clicked', value: c.stats.clicked },
                      { label: 'Bounced', value: c.stats.bounced },
                    ].map((s, i) => (
                      <div key={i} className="text-center">
                        <p className="text-xs font-bold text-gray-900">{s.value}</p>
                        <p className="text-[9px] text-gray-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {campaigns.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">
              <Send className="h-10 w-10 mx-auto mb-2 text-gray-700" />
              No campaigns yet. Create your first email campaign.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════ HOLIDAY PLANNER ═══════════════════
function HolidayPlannerView({ getHeaders, hdrs }) {
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/client/dashboard/holiday-planner`, { headers: getHeaders() })
      .then(r => r.json()).then(d => { setHolidays(d.holidays || []); setYear(d.year); })
      .catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const createCampaign = async (holiday) => {
    await fetch(`${API}/api/client/dashboard/holiday-planner/${holiday.full_date}/create-campaign`, {
      method: 'POST', headers: hdrs(), body: JSON.stringify({ name: `${holiday.name} Campaign` }),
    });
    fetch_();
  };

  if (loading) return <div className="text-gray-400 animate-pulse text-center py-8 text-sm">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="holiday-planner-view">
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-blue-400" />
        <p className="text-sm font-semibold text-gray-900">Holiday Campaign Planner {year}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {holidays.map((h, i) => {
          const isPast = new Date(`${h.full_date}T23:59:59`) < new Date();
          return (
            <Card key={i} className={`bg-white border-gray-200 ${isPast ? 'opacity-50' : ''}`} data-testid={`holiday-${h.date}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{h.emoji}</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 font-medium">{h.name}</p>
                    <p className="text-[10px] text-gray-500">{new Date(`${h.full_date}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
                  </div>
                  {h.has_campaign ? (
                    <Badge className={`text-[9px] capitalize ${STATUS_STYLES[h.campaign_status] || STATUS_STYLES.draft}`}>{h.campaign_status}</Badge>
                  ) : !isPast ? (
                    <Button size="sm" onClick={() => createCampaign(h)} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 text-[10px] h-6 px-2" data-testid={`create-holiday-${h.date}`}>
                      <Plus className="h-2.5 w-2.5 mr-0.5" /> Plan
                    </Button>
                  ) : (
                    <Badge className="bg-gray-800 text-gray-500 text-[9px]">Passed</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
